/**
 * Linkified — renders chat / message text with:
 *   • `**bold**`           → <strong>
 *   • `*italic*` / `_em_`  → <em>
 *   • `\`code\``           → <code>
 *   • http(s):// URLs      → clickable <a target="_blank" rel="noopener noreferrer">
 *   • Preserves whitespace and line breaks.
 *
 * SECURITY: every link is target="_blank" rel="noopener noreferrer"; no
 * arbitrary HTML is interpreted — only the markdown tokens listed above.
 * Used everywhere we display LLM/agent output (ChatWidget, etc.).
 */
import React from "react";

const URL_RE = /\b((?:https?:\/\/|www\.)[^\s<>"']+)/gi;

function cleanTrailing(url) {
    const trailing = /[.,;:!?)\]]+$/;
    const m = url.match(trailing);
    if (!m) return { href: url, trail: "" };
    return { href: url.slice(0, -m[0].length), trail: m[0] };
}

// Recursively tokenize a string. Order matters: bold first (greediest
// delimiter), then code, then italic, then linkify. Each level only acts on
// PLAIN STRING children — nested React nodes are passed through untouched.
function processTokens(input, processor) {
    if (typeof input === "string") return processor(input);
    if (Array.isArray(input)) return input.flatMap(c => processTokens(c, processor));
    return [input];
}

function splitBold(str) {
    const out = [];
    const re = /\*\*([^*\n][^*]*?)\*\*/g;
    let last = 0; let m;
    while ((m = re.exec(str)) !== null) {
        if (m.index > last) out.push(str.slice(last, m.index));
        out.push(<strong key={`b-${m.index}`}>{m[1]}</strong>);
        last = m.index + m[0].length;
    }
    if (last < str.length) out.push(str.slice(last));
    return out.length ? out : [str];
}

function splitCode(str) {
    const out = [];
    const re = /`([^`\n]+?)`/g;
    let last = 0; let m;
    while ((m = re.exec(str)) !== null) {
        if (m.index > last) out.push(str.slice(last, m.index));
        out.push(
            <code key={`c-${m.index}`} className="px-1 py-0.5 rounded bg-muted text-foreground/90 font-mono text-[0.9em]">
                {m[1]}
            </code>,
        );
        last = m.index + m[0].length;
    }
    if (last < str.length) out.push(str.slice(last));
    return out.length ? out : [str];
}

function splitItalic(str) {
    // *italic* — single asterisks that are NOT part of bold (we already
    // consumed those). Must not match `**`. Also support `_italic_`.
    const out = [];
    const re = /(?<![*\w])\*([^*\n]+?)\*(?!\*)|(?<![\w_])_([^_\n]+?)_(?![\w_])/g;
    let last = 0; let m;
    while ((m = re.exec(str)) !== null) {
        if (m.index > last) out.push(str.slice(last, m.index));
        out.push(<em key={`i-${m.index}`}>{m[1] ?? m[2]}</em>);
        last = m.index + m[0].length;
    }
    if (last < str.length) out.push(str.slice(last));
    return out.length ? out : [str];
}

function splitLinks(str) {
    const out = [];
    URL_RE.lastIndex = 0;
    let last = 0; let m;
    while ((m = URL_RE.exec(str)) !== null) {
        if (m.index > last) out.push(str.slice(last, m.index));
        const { href, trail } = cleanTrailing(m[0]);
        const fullHref = href.startsWith("http") ? href : `https://${href}`;
        out.push(
            <a
                key={`l-${m.index}`}
                href={fullHref}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline underline-offset-2 hover:text-primary/80 break-all"
            >
                {href}
            </a>,
        );
        if (trail) out.push(trail);
        last = m.index + m[0].length;
    }
    if (last < str.length) out.push(str.slice(last));
    return out.length ? out : [str];
}

export default function Linkified({ text, className = "", as: Tag = "span" }) {
    if (text == null) return null;
    let nodes = [String(text)];
    nodes = processTokens(nodes, splitBold);
    nodes = processTokens(nodes, splitCode);
    nodes = processTokens(nodes, splitItalic);
    nodes = processTokens(nodes, splitLinks);
    return (
        <Tag className={className}>
            {nodes.map((n, i) => (typeof n === "string" ? <React.Fragment key={i}>{n}</React.Fragment> : n))}
        </Tag>
    );
}
